import React, { useCallback, useEffect, useState } from 'react';
import { ChevronUp, ChevronDown, Plus, Trash2, Eye, EyeOff, Loader2, Check, X } from 'lucide-react';
import { apiClient } from '../../../lib/apiClient';
import { apiError } from './apiError';

/** Create, rename, reorder, deactivate and delete directory categories. */
const CategoriesPanel = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);
  const [renaming, setRenaming] = useState(null); // { id, name }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/directory/categories');
      setCategories(Array.isArray(res.data) ? res.data : []);
      setError(null);
    } catch (err) {
      setError(apiError(err, 'Could not load categories.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const run = async (fn, fallback) => {
    setBusy(true);
    try {
      await fn();
      setError(null);
      await load();
    } catch (err) {
      setError(apiError(err, fallback));
    } finally {
      setBusy(false);
    }
  };

  const add = (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    run(async () => {
      await apiClient.post('/directory/categories', { name: newName });
      setNewName('');
    }, 'Could not add the category.');
  };

  const saveRename = () => {
    if (!renaming?.name.trim()) return;
    run(async () => {
      await apiClient.put(`/directory/categories/${renaming.id}`, { name: renaming.name });
      setRenaming(null);
    }, 'Could not rename the category.');
  };

  const toggleActive = (c) =>
    run(() => apiClient.put(`/directory/categories/${c.id}`, { active: !c.active }), 'Could not update the category.');

  const remove = (c) => {
    if (!window.confirm(`Delete the category "${c.name}"? This cannot be undone.`)) return;
    run(() => apiClient.delete(`/directory/categories/${c.id}`), 'Could not delete the category.');
  };

  const move = (index, delta) => {
    const next = [...categories];
    const target = index + delta;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setCategories(next);
    run(() => apiClient.post('/directory/categories/reorder', { ids: next.map((c) => c.id) }), 'Could not save the order.');
  };

  return (
    <div data-testid="directory-categories">
      <form onSubmit={add} className="flex flex-col sm:flex-row gap-2 mb-4">
        <input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          maxLength={80}
          placeholder="New category name"
          aria-label="New category name"
          className="flex-1 px-3 py-2 min-h-[44px] rounded-lg bg-white border border-gray-200 text-gray-900 text-sm focus:border-[#FF6B00] outline-none"
          data-testid="category-new-name"
        />
        <button
          type="submit"
          disabled={busy || !newName.trim()}
          className="inline-flex items-center justify-center gap-2 px-4 py-2 min-h-[44px] rounded-lg bg-[#FF6B00] hover:bg-[#ff8124] disabled:opacity-50 text-[#1a1c1b] font-semibold text-sm"
          data-testid="category-add"
        >
          <Plus className="w-4 h-4" /> Add category
        </button>
      </form>

      {error && (
        <div className="mb-4 px-4 py-3 rounded-lg border border-red-200 bg-red-50 text-red-600 text-sm" role="alert">{error}</div>
      )}

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
      ) : categories.length === 0 ? (
        <p className="text-gray-500 text-sm py-8 text-center">No categories yet. Add the first one above.</p>
      ) : (
        <ul className="space-y-2">
          {categories.map((c, i) => (
            <li key={c.id} className="flex items-center gap-2 sm:gap-3 p-3 rounded-xl border border-gray-200 bg-white" data-testid={`category-row-${c.id}`}>
              <div className="flex flex-col">
                <button type="button" onClick={() => move(i, -1)} disabled={i === 0 || busy} aria-label={`Move ${c.name} up`} className="p-1 text-gray-500 hover:text-[#FF6B00] disabled:opacity-30"><ChevronUp className="w-4 h-4" /></button>
                <button type="button" onClick={() => move(i, 1)} disabled={i === categories.length - 1 || busy} aria-label={`Move ${c.name} down`} className="p-1 text-gray-500 hover:text-[#FF6B00] disabled:opacity-30"><ChevronDown className="w-4 h-4" /></button>
              </div>

              <div className="flex-1 min-w-0">
                {renaming?.id === c.id ? (
                  <input
                    autoFocus
                    value={renaming.name}
                    maxLength={80}
                    onChange={(e) => setRenaming({ ...renaming, name: e.target.value })}
                    onKeyDown={(e) => { if (e.key === 'Enter') saveRename(); if (e.key === 'Escape') setRenaming(null); }}
                    aria-label={`Rename ${c.name}`}
                    className="w-full px-2 py-1.5 rounded-lg bg-gray-50 border border-gray-200 text-sm focus:border-[#FF6B00] outline-none"
                  />
                ) : (
                  <>
                    <p className={`text-sm font-semibold truncate ${c.active ? 'text-gray-900' : 'text-gray-400'}`}>{c.name}</p>
                    <p className="text-gray-500 text-xs">
                      {c.listing_count} listing{c.listing_count === 1 ? '' : 's'}{c.active ? '' : ' · inactive'}
                    </p>
                  </>
                )}
              </div>

              {renaming?.id === c.id ? (
                <>
                  <button type="button" onClick={saveRename} aria-label="Save name" className="p-2 text-[#FF6B00]"><Check className="w-4 h-4" /></button>
                  <button type="button" onClick={() => setRenaming(null)} aria-label="Cancel rename" className="p-2 text-gray-500"><X className="w-4 h-4" /></button>
                </>
              ) : (
                <>
                  <button type="button" onClick={() => toggleActive(c)} aria-pressed={c.active} aria-label={`${c.active ? 'Deactivate' : 'Activate'} ${c.name}`} className={`p-2 ${c.active ? 'text-[#FF6B00]' : 'text-gray-500 hover:text-gray-900'}`}>
                    {c.active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  </button>
                  <button type="button" onClick={() => setRenaming({ id: c.id, name: c.name })} className="px-3 py-2 min-h-[44px] rounded-lg border border-gray-200 text-gray-700 text-xs hover:border-[#FF6B00] hover:text-[#FF6B00]">Rename</button>
                  <button type="button" onClick={() => remove(c)} aria-label={`Delete ${c.name}`} className="p-2 text-gray-500 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default CategoriesPanel;
